import type { BadgeVariant } from '../../components/ui/badge';
import type {
  ReportingBackfill,
  ReportingBackfillStatus,
  ReportingPullMode,
} from '../../types/reporting';

// Pure view logic for the Backfills page (Build B) — status→chip, newest-first
// sort, and the "any running?" predicate that drives the 10s poll.

export interface BackfillStatusChip {
  label: string;
  variant: BadgeVariant;
}

const STATUS_CHIP: Record<ReportingBackfillStatus, BackfillStatusChip> = {
  running: { label: 'Running', variant: 'warning' },
  succeeded: { label: 'Succeeded', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

export function backfillStatusChip(status: ReportingBackfillStatus): BackfillStatusChip {
  return STATUS_CHIP[status];
}

// True when any record is still running — the page polls while this holds.
export function anyBackfillRunning(backfills: readonly ReportingBackfill[]): boolean {
  return backfills.some((b) => b.status === 'running');
}

// Newest-first by createdAt (the server already orders, but the UI shouldn't
// depend on server order). Returns a new array; stable on equal timestamps.
export function sortBackfillsNewestFirst(
  backfills: readonly ReportingBackfill[],
): ReportingBackfill[] {
  return [...backfills].sort((a, b) => {
    const at = Date.parse(a.createdAt);
    const bt = Date.parse(b.createdAt);
    const av = Number.isNaN(at) ? 0 : at;
    const bv = Number.isNaN(bt) ? 0 : bt;
    return bv - av;
  });
}

// Does this account currently have a running backfill? Drives the account-page
// button's disabled/replaced state so a second concurrent trigger is blocked.
export function accountHasRunningBackfill(
  backfills: readonly ReportingBackfill[],
  accountId: string,
): boolean {
  return backfills.some((b) => b.accountId === accountId && b.status === 'running');
}

// The account's currently-running backfill record (the newest, if somehow >1), so
// the Analytics Coverage section can annotate WHICH tier is in flight. Null when
// none is running.
export function accountRunningBackfill(
  backfills: readonly ReportingBackfill[],
  accountId: string,
): ReportingBackfill | null {
  const running = backfills.filter((b) => b.accountId === accountId && b.status === 'running');
  if (running.length === 0) return null;
  return sortBackfillsNewestFirst(running)[0]!;
}

// Human label for a tiered pull mode (Phase 1). A legacy full backfill (null
// pullMode) reads as "Full history".
// ⚠️ The demographics labels say "daily" because the modes now pull day by day
// (one exact-day window each) rather than one wide window. The distinction is the
// whole reason to press the button: only a daily row puts the audience panel on
// the daily path, so a label that read "90d" would describe the grain that was
// the problem.
const PULL_MODE_LABELS: Record<ReportingPullMode, string> = {
  'structural-recent': 'Structural · recent (90d)',
  'structural-full': 'Structural · full (25mo)',
  'demographics-recent': 'Demographics · recent (90d, daily)',
  'demographics-full': 'Demographics · full (13mo, daily)',
};

export function pullModeLabel(mode: ReportingPullMode | null): string {
  return mode ? PULL_MODE_LABELS[mode] : 'Full history';
}
